/**
 * The controls both inbox toolbars wear (§11.4).
 *
 * The design draws one control strip with a single slot that differs:
 *
 *   personal   [Compose] · All mail ● Gmail ● Outlook  ⟶  count Filter Sort Sync ⚙
 *   unified    [Compose] · «mailbox filter»            ⟶  count Filter Sort Sync ⚙
 *
 * `PersonalInboxToolbar` and `InboxFiltersBar` keep their own shells — each
 * owns a different popover set and a different filter vocabulary — but every
 * control OUTSIDE that slot is the same on both, and was drifting: the two had
 * different Sync buttons, different count spellings, and only one had Compose.
 * They live here now so there is one of each.
 */
import React from 'react';
import { ArrowDownNarrowWide, Check, Filter, RefreshCw, Send, Settings2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SORT_OPTIONS } from './mailSortOptions';

/**
 * The design's icon button: 36×36, one hairline border, and a DARK FILL when
 * it is active — a menu that is open, or a filter that is applied. The fill is
 * the only signal that a filter is on once the label is gone, so it is not
 * decoration: an icon-only Filters button with no active state is a control
 * that hides the fact that the list is filtered.
 */
export const ICON_BUTTON_BASE =
    'flex items-center justify-center w-9 h-9 rounded-[9px] border flex-shrink-0 transition-colors ' +
    'disabled:opacity-50 disabled:cursor-not-allowed';
const ICON_BUTTON_ON = 'border-[#0C0A09] bg-[#0C0A09] text-[#FBFAF9]';
const ICON_BUTTON_OFF = 'border-[#E4E1DB] bg-white text-[#57504B] hover:bg-[#FBFAF9]';

export function iconButtonClass(active) {
    return `${ICON_BUTTON_BASE} ${active ? ICON_BUTTON_ON : ICON_BUTTON_OFF}`;
}

/** The design's violet primary. Used nowhere else in the inbox. */
export function ComposeButton({ onClick, label = 'Compose', testId = 'inbox-compose' }) {
    return (
        <Button
            size="sm"
            onClick={onClick}
            data-testid={testId}
            className="bg-[#4D11F6] hover:bg-[#3B0DBE] text-white h-9 rounded-[9px] flex-shrink-0"
        >
            <Send className="w-4 h-4 mr-1.5" />
            {label}
        </Button>
    );
}

/** "40 of 394" — a label, not a pagination control: §1.4 made both lists infinite. */
export function InboxCountLabel({ shown, total, testId = 'inbox-count' }) {
    if (typeof shown !== 'number') return null;
    return (
        <span
            className="text-[12px] text-[#A29B95] flex-shrink-0 whitespace-nowrap tabular-nums"
            data-testid={testId}
        >
            {shown} of {typeof total === 'number' ? total : '?'}
        </span>
    );
}

/**
 * The Filters trigger — icon only, and lit while any filter is applied.
 *
 * The popover CONTENT stays with each bar: the two lanes filter on different
 * things (`unread`/`provider` here, `replied`/`mailbox` there) and folding
 * those vocabularies together is the one thing both bars' headers warn against.
 * Only the trigger is shared, because only the trigger is the same.
 */
export const InboxFilterButton = React.forwardRef(function InboxFilterButton(
    { activeCount = 0, open = false, testId = 'inbox-filters-trigger', ...props },
    ref,
) {
    const title = activeCount
        ? `Filters — ${activeCount} applied`
        : 'Filters';
    return (
        // Spreads and forwards: this is a PopoverTrigger `asChild` on both bars,
        // and Radix needs the ref and its own handlers to reach the element.
        <button
            {...props}
            ref={ref}
            type="button"
            title={title}
            aria-label={title}
            data-testid={testId}
            className={`relative ${iconButtonClass(open || activeCount > 0)}`}
        >
            <Filter className="w-4 h-4" />
            {activeCount > 0 && (
                <span
                    className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full
                               bg-[#4D11F6] text-white text-[10px] leading-4 text-center tabular-nums"
                    data-testid={`${testId}-count`}
                >
                    {activeCount}
                </span>
            )}
        </button>
    );
});

/**
 * The Sort menu — icon only, the design's four orderings, one check mark.
 *
 * Presentational: it takes the active key and hands back the key that was
 * chosen. Which filter parameters that key maps to is the caller's business,
 * because it differs per lane (`mailSortOptions.js` holds both adapters).
 */
export function InboxSortMenu({ value, onChange, testId = 'inbox-sort' }) {
    const [open, setOpen] = React.useState(false);
    const active = SORT_OPTIONS.find((o) => o.key === value) || SORT_OPTIONS[0];
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title={`Sort — ${active.label}`}
                    aria-label={`Sort — ${active.label}`}
                    data-testid={testId}
                    className={iconButtonClass(open)}
                >
                    <ArrowDownNarrowWide className="w-4 h-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1.5 rounded-[11px]">
                <div className="px-2.5 pt-2 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-[#A29B95]">
                    Sort by
                </div>
                {SORT_OPTIONS.map((option) => {
                    const on = option.key === active.key;
                    return (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => { onChange?.(option.key); setOpen(false); }}
                            data-testid={`${testId}-${option.key}`}
                            aria-checked={on}
                            role="menuitemradio"
                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left
                                        text-[13.5px] text-[#0C0A09] hover:bg-[#F6F5F1]
                                        ${on ? 'font-semibold' : 'font-[450]'}`}
                        >
                            <span className="flex-1">{option.label}</span>
                            {on && <Check className="w-[15px] h-[15px] text-[#432DD7]" />}
                        </button>
                    );
                })}
            </PopoverContent>
        </Popover>
    );
}

/** One Sync for every connected mailbox — never one per provider. */
export function SyncButton({ onClick, isSyncing = false, canSync = true, testId = 'inbox-sync' }) {
    const title = canSync ? 'Sync connected mailboxes' : 'No mailbox connected';
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isSyncing || !canSync}
            title={isSyncing ? 'Syncing…' : title}
            aria-label={title}
            data-testid={testId}
            className={iconButtonClass(false)}
        >
            {/* The spinner is the whole sync indicator now that the banner is
                gone from every sync after the first — so it animates on a
                background sync too, not only on one the user asked for. */}
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
        </button>
    );
}

/** ⚙ — the settings surface for whichever inbox is on screen. */
export function InboxSettingsButton({ onClick, title = 'Inbox settings' }) {
    if (!onClick) return null;
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={title}
            data-testid="inbox-settings"
            className={iconButtonClass(false)}
        >
            <Settings2 className="w-4 h-4" />
        </button>
    );
}
